import { useMutation, useQueryClient } from "@tanstack/react-query";
import { rejectAtsApplication } from "@/lib/api/generated";
import { showToast } from "@/components/ui/sonner";

interface RejectVars {
  candidateId: string;
  rejectionReasonId: string;
  note?: string | null;
}

/** Thin connection-scoped wrapper over the generated reject endpoint (spec §8). */
export function useRejectInAts(connectionId: string) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: ({ candidateId, rejectionReasonId, note }: RejectVars) =>
      rejectAtsApplication(connectionId, candidateId, {
        rejection_reason_id: rejectionReasonId,
        note: note ?? null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pipeline"] });
    },
  });

  return {
    reject: async (vars: RejectVars) => {
      // The reject endpoint THROWS on any failure (the API client raises on
      // every non-2xx — 409 blocked/reason-required/in-progress, 404 not
      // found); a success resolves with ok:true. So surface failures from the
      // catch, not a dead `if (!result.ok)` branch, and rethrow so the modal
      // stays open for the user to retry (review-2 finding #4).
      try {
        await mutation.mutateAsync(vars);
      } catch (err) {
        const description =
          err instanceof Error ? err.message : "The ATS rejected this action.";
        showToast.error("Couldn't reject in ATS", { description });
        throw err;
      }
    },
    isPending: mutation.isPending,
  };
}
